import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import './styles/tokens.css'
import { requireSession } from '@/app/auth'
import { Setari } from '@/modules/setari'
import { Shell } from '@/app/shell'
import { Invoices } from '@/modules/money/invoices'
import { Receipts } from '@/modules/money/receipts'
import { Payments } from '@/modules/money/payments'
import { Reservations } from '@/modules/sales/reservations'
import { Partners } from '@/modules/partners/partners'
import { Dashboard } from '@/modules/overview/dashboard'
import { InvoiceDetail } from '@/modules/money/invoice-detail'
import { ReservationDetail } from '@/modules/sales/reservation-detail'
import { PartnerDetail } from '@/modules/partners/partner-detail'
import { Advances } from '@/modules/money/advances'
import { CashRegister } from '@/modules/money/cash-register'
import { Departures } from '@/modules/sales/departures'
import { Rapoarte } from '@/modules/overview/rapoarte'
import { Sabloane } from '@/modules/sabloane'
import { Crm } from '@/modules/partners/crm'
import { Grupuri } from '@/modules/grupuri/grupuri'
import { GrupDetaliu } from '@/modules/grupuri/grup-detaliu'
import { GrupNou } from '@/modules/nou/grup'
import { WizardRezervare } from '@/modules/nou/wizard'
import { Aniversari } from '@/modules/marketing/aniversari'
import { Itinerar } from '@/modules/sales/itinerar'
import { InscriereGrup } from '@/modules/public/inscriere'
import { Tasks } from '@/modules/overview/tasks'
import { Calendar } from '@/modules/overview/calendar'
import { Offers } from '@/modules/sales/offers'
import { OfertaNoua } from '@/modules/nou/oferta'
import { RezervareNoua } from '@/modules/nou/rezervare'
import { FacturaNoua } from '@/modules/nou/factura'
import { ClientNou } from '@/modules/nou/client'

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 } },
})

const PUBLIC = '/app/inscriere/'

if (location.pathname.startsWith(PUBLIC)) {
  // Linkul de inscriere in grup e singura pagina fara autentificare: il primeste
  // turistul, nu operatorul, deci nu trece prin `requireSession` si nu are meniuri.
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={qc}>
        <BrowserRouter basename="/app">
          <Routes>
            <Route path="/inscriere/:token" element={<InscriereGrup />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  )
} else if (requireSession()) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={qc}>
        <BrowserRouter basename="/app">
          <Shell>
            <Routes>
              <Route path="/" element={<Navigate to="/tablou" replace />} />
              <Route path="/tablou" element={<Dashboard />} />
              <Route path="/sarcini" element={<Tasks />} />
              <Route path="/calendar" element={<Calendar />} />

              <Route path="/contacte" element={<Partners />} />
              <Route path="/contacte/oportunitati" element={<Crm />} />
              <Route path="/contacte/:id" element={<PartnerDetail />} />

              <Route path="/rezervari" element={<Reservations />} />
              <Route path="/rezervari/plecari" element={<Departures />} />
              <Route path="/rezervari/oferte" element={<Offers />} />
              <Route path="/rezervari/grupuri" element={<Grupuri />} />
              <Route path="/rezervari/grupuri/:id" element={<GrupDetaliu />} />
              <Route path="/rezervari/:id" element={<ReservationDetail />} />
              <Route path="/rezervari/:id/program" element={<Itinerar />} />

              <Route path="/bani" element={<Navigate to="/bani/facturi" replace />} />
              <Route path="/bani/facturi" element={<Invoices />} />
              <Route path="/bani/facturi/:id" element={<InvoiceDetail />} />
              <Route path="/bani/incasari" element={<Receipts />} />
              <Route path="/bani/plati" element={<Payments />} />
              <Route path="/bani/avansuri" element={<Advances />} />
              <Route path="/bani/casa" element={<CashRegister />} />

              <Route path="/marketing" element={<Aniversari />} />
              <Route path="/rapoarte" element={<Rapoarte copil="ansamblu" />} />
              <Route path="/rapoarte/vanzari" element={<Rapoarte copil="vanzari" />} />
              <Route path="/rapoarte/clienti" element={<Rapoarte copil="clienti" />} />
              <Route path="/rapoarte/furnizori" element={<Rapoarte copil="furnizori" />} />
              <Route path="/rapoarte/bani" element={<Rapoarte copil="bani" />} />
              <Route path="/sabloane" element={<Sabloane copil="documente" />} />
              <Route path="/sabloane/antet" element={<Sabloane copil="antet" />} />
              <Route path="/sabloane/penalizari" element={<Sabloane copil="penalizari" />} />
              <Route path="/sabloane/mesaje" element={<Sabloane copil="mesaje" />} />
              <Route path="/setari" element={<Setari copil="utilizatori" />} />
              <Route path="/setari/tva" element={<Setari copil="tva" />} />
              <Route path="/setari/observatii" element={<Setari copil="observatii" />} />
              <Route path="/setari/email" element={<Setari copil="email" />} />
              <Route path="/setari/sincronizare" element={<Setari copil="sincronizare" />} />
              <Route path="/setari/saga" element={<Setari copil="saga" />} />
              <Route path="/setari/joburi" element={<Setari copil="joburi" />} />
              <Route path="/setari/import" element={<Setari copil="import" />} />

              <Route path="/nou/oferta" element={<OfertaNoua />} />
              <Route path="/nou/rezervare" element={<RezervareNoua />} />
              <Route path="/nou/factura" element={<FacturaNoua />} />
              <Route path="/nou/client" element={<ClientNou />} />
              <Route path="/nou/grup" element={<GrupNou />} />
              <Route path="/nou/rezervare-wizard" element={<WizardRezervare />} />

              {/* adresele vechi raman valabile */}
              <Route path="/vanzari/rezervari" element={<Navigate to="/rezervari" replace />} />
              <Route path="/vanzari/plecari" element={<Navigate to="/rezervari/plecari" replace />} />
              <Route path="/parteneri" element={<Navigate to="/contacte" replace />} />
              <Route path="/parteneri/oportunitati" element={<Navigate to="/contacte/oportunitati" replace />} />
              <Route path="/analiza/rapoarte" element={<Navigate to="/rapoarte" replace />} />
              <Route path="/admin/utilizatori" element={<Navigate to="/setari" replace />} />
              <Route path="*" element={<Navigate to="/tablou" replace />} />
            </Routes>
          </Shell>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  )
}
